import {
    createStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
    WithStyles,
    withStyles
} from "@material-ui/core";
import { useGetAuctionWatchersQuery } from "../../../../api/auctionApiSlice";
import { formatAuctionDateTime } from "../../../../helpers/auctionDateTime";
import { IAuctionWatcher } from "../../../../models/auction/IAuctionWatcher";
import TableFooter from "../../../shared/TableFooter";

interface IAuctionWatchersTab extends WithStyles<typeof styles> {
    auctionId: string;
}

const styles = (theme: Theme) =>
    createStyles({
        tableCell: {
            borderColor: theme.palette.divider
        },
        tableRow: {
            "&:nth-of-type(odd)": {
                backgroundColor: theme.palette.background.default
            },
            borderColor: theme.palette.grey[500]
        },
        tableCellHeader: { borderColor: "transparent" },
        tableHeader: { fontWeight: "bold" },
        formSection: {
            marginBottom: theme.spacing(5),
            backgroundColor: theme.palette.common.black,
            padding: "32px"
        }
    });

interface Column<T> {
    name: string;
    hideOnMobile?: boolean;
    value: (row: T) => string;
}

const COLS: Column<IAuctionWatcher>[] = [
    { name: "Name", value: (row) => `${row.firstName} ${row.lastName}` },
    { name: "Email", value: (row) => row.email, hideOnMobile: true },
    {
        name: "Last Seen",
        value: (row) =>
            formatAuctionDateTime(row.lastSeenAt)?.mediumDateTime ?? "",
        hideOnMobile: true
    },
    {
        name: "Online",
        value: (row) => (row.isOnline ? "Yes" : "No")
    }
];

const NUM_DESKTOP_COLS = COLS.length;
const NUM_MOBILE_COLS = COLS.filter((col) => !col.hideOnMobile).length;
export const POLLING_INTERVAL_MS = 15 * 1000; // 15 seconds

function AuctionWatchersTab({ auctionId, classes }: IAuctionWatchersTab) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const query = useGetAuctionWatchersQuery(auctionId, {
        pollingInterval: POLLING_INTERVAL_MS,
        skipPollingIfUnfocused: true,
        refetchOnFocus: true
    });

    if (query.error) {
        return (
            <div className={classes.formSection}>
                <Typography variant="body2" color="error">
                    An error occurred while fetching sale watchers.
                </Typography>
            </div>
        );
    }

    const watchersOnlineCount =
        query.data?.filter((watcher) => watcher.isOnline).length ?? 0;

    const headerColSpan = isMobile ? NUM_MOBILE_COLS / 2 : NUM_DESKTOP_COLS / 2;

    return (
        <>
            <div className={classes.formSection} />
            <div className={classes.formSection}>
                <Table data-testid="auctionWatchersTable">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                className={classes.tableCellHeader}
                                colSpan={headerColSpan}
                            >
                                <Typography
                                    className={classes.tableHeader}
                                    variant="subtitle1"
                                >
                                    Sale Watchers
                                </Typography>
                            </TableCell>
                            <TableCell
                                className={classes.tableCellHeader}
                                colSpan={headerColSpan}
                                align="right"
                            >
                                <Typography
                                    className={classes.tableHeader}
                                    variant="subtitle1"
                                >
                                    {`Total Online: ${watchersOnlineCount}`}
                                </Typography>
                            </TableCell>
                        </TableRow>

                        <TableRow>
                            {COLS.map((col) => {
                                if (isMobile && col.hideOnMobile) return null;
                                return (
                                    <TableCell
                                        key={col.name}
                                        className={classes.tableCell}
                                        size="small"
                                    >
                                        {col.name}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {query.data?.length ? (
                            query.data?.map((watcher) => (
                                <TableRow
                                    key={watcher.userId}
                                    className={classes.tableRow}
                                >
                                    {COLS.map((col) => {
                                        if (isMobile && col.hideOnMobile)
                                            return null;
                                        return (
                                            <TableCell
                                                key={col.name}
                                                className={classes.tableCell}
                                                size="small"
                                            >
                                                {col.value(watcher)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    className={classes.tableCell}
                                    size="small"
                                    colSpan={
                                        isMobile
                                            ? NUM_MOBILE_COLS
                                            : NUM_DESKTOP_COLS
                                    }
                                >
                                    <Typography
                                        component="div"
                                        variant="body2"
                                        align="center"
                                        color="textSecondary"
                                    >
                                        No watchers for this sale yet.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>

                    <TableFooter
                        colSpan={isMobile ? NUM_MOBILE_COLS : NUM_DESKTOP_COLS}
                        count={query.data?.length ?? 0}
                        helperText="A watcher is a user who is logged in and viewed the sale bidding page today."
                    />
                </Table>
            </div>
        </>
    );
}

export default withStyles(styles)(AuctionWatchersTab);
